import { useState, useEffect } from 'react';
import { cn } from '../utils/cn';
import {
  Wallet,
  TrendingUp,
  Lock,
  Coins,
  Gift,
  Info,
  CheckCircle2,
  Star,
  Zap,
  BarChart3,
  Calendar,
  Sparkles,
  Award,
  AlertTriangle,
  XCircle,
} from 'lucide-react';

// Staking tier configuration - planned tokenomics
const stakingTiers = [
  { name: 'Flexible', duration: '0 days', multiplier: '1.0x', apy: '8%' },
  { name: '1 Month', duration: '30 days', multiplier: '1.1x', apy: '10%' },
  { name: '3 Months', duration: '90 days', multiplier: '1.25x', apy: '14%' },
  { name: '6 Months', duration: '180 days', multiplier: '1.5x', apy: '18%' },
  { name: '1 Year', duration: '365 days', multiplier: '2.0x', apy: '25%' },
];

const holdingTiers = [
  { name: 'Standard', min: '0', discount: '0%', benefits: ['Basic trading'] },
  { name: 'Bronze', min: '1,000', discount: '10%', benefits: ['10% fee discount', 'Priority support'] },
  { name: 'Silver', min: '10,000', discount: '25%', benefits: ['25% fee discount', 'Advanced bots', 'API access'] },
  { name: 'Gold', min: '100,000', discount: '40%', benefits: ['40% fee discount', 'Premium AI models', 'Dedicated manager'] },
  { name: 'Platinum', min: '1,000,000', discount: '50%', benefits: ['50% fee discount', 'Custom strategies', 'OTC desk'] },
  { name: 'Diamond', min: '10,000,000', discount: '60%', benefits: ['60% fee discount', 'Revenue share', 'Governance votes'] },
];

// Revenue sources - will be populated from blockchain
const revenueSources = [
  { name: 'Trading Fees', icon: TrendingUp },
  { name: 'Mining Contracts', icon: Coins },
  { name: 'AI Server Rental', icon: Zap },
  { name: 'Premium Subs', icon: Star },
];

export default function DeFi() {
  const [activeTab, setActiveTab] = useState<'staking' | 'farming' | 'token'>('staking');
  const [selectedTier, setSelectedTier] = useState(2); // 3 months default
  const [stakeAmount, setStakeAmount] = useState('');
  const [walletBalance] = useState(0); // Will be populated from wallet connection
  
  // Contract deployment status - will be true when deployed
  const [isContractDeployed, setIsContractDeployed] = useState(false);
  
  // Check deployment status on mount
  useEffect(() => {
    const checkDeployment = () => {
      const savedContracts = localStorage.getItem('hbx_contracts');
      if (savedContracts) {
        const contracts = JSON.parse(savedContracts);
        setIsContractDeployed(!!contracts.staking);
      }
    };
    checkDeployment();
  }, []);

  // Calculate days until next distribution (for display purposes)
  const now = new Date();
  const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  const daysUntilDistribution = Math.ceil((endOfMonth.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="h-5 w-5 text-primary" />
          <span className="text-sm font-medium text-primary">DeFi Hub</span>
        </div>
        <h1 className="font-heading text-3xl font-bold text-text-primary mb-2">
          Stake, Earn & Govern
        </h1>
        <p className="text-text-secondary">
          Stake HBX tokens to earn platform revenue and unlock premium features
        </p>
      </div>

      {/* Pre-Deployment Warning */}
      {!isContractDeployed && (
        <div className="mb-6 p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-medium text-amber-500">Pre-Deployment Status</div>
            <p className="text-sm text-text-secondary mt-1">
              HBX Staking contracts are not deployed yet. The features below show planned functionality. 
              All data will be real once contracts are deployed to mainnet.
            </p>
          </div>
        </div>
      )}

      {/* Quick Stats */}
      <div className="grid grid-cols-4 gap-4 mb-8">
        <div className="stat-card">
          <div className="flex items-center justify-between mb-3">
            <Wallet className="h-5 w-5 text-primary" />
            <span className="text-xs text-text-tertiary">Balance</span>
          </div>
          <div className="font-heading text-2xl font-bold text-text-primary">
            {isContractDeployed ? '0' : '—'} <span className="text-sm text-text-secondary">HBX</span>
          </div>
          <div className="text-sm text-text-tertiary mt-1">{isContractDeployed ? '≈ $0.00' : 'Connect wallet'}</div>
        </div>

        <div className="stat-card">
          <div className="flex items-center justify-between mb-3">
            <Lock className="h-5 w-5 text-accent" />
            <span className="text-xs text-text-tertiary">Staked</span>
          </div>
          <div className="font-heading text-2xl font-bold text-text-primary">
            {isContractDeployed ? '0' : '—'} <span className="text-sm text-text-secondary">HBX</span>
          </div>
          <div className="text-sm text-text-tertiary mt-1">{isContractDeployed ? 'No stake yet' : 'Awaiting deployment'}</div>
        </div>

        <div className="stat-card">
          <div className="flex items-center justify-between mb-3">
            <Gift className="h-5 w-5 text-success" />
            <span className="text-xs text-text-tertiary">Pending</span>
          </div>
          <div className="font-heading text-2xl font-bold text-text-primary">
            {isContractDeployed ? '0.00' : '—'} <span className="text-sm text-text-secondary">HBX</span>
          </div>
          <div className="text-sm text-text-tertiary mt-1">{isContractDeployed ? `Next: ${daysUntilDistribution} days` : 'Awaiting deployment'}</div>
        </div>

        <div className="stat-card">
          <div className="flex items-center justify-between mb-3">
            <Award className="h-5 w-5 text-warning" />
            <span className="text-xs text-text-tertiary">Your Tier</span>
          </div>
          <div className="font-heading text-2xl font-bold text-text-primary">{isContractDeployed ? 'Standard' : '—'}</div>
          <div className="text-sm text-text-tertiary mt-1">{isContractDeployed ? 'Hold HBX to upgrade' : 'Awaiting deployment'}</div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 mb-6">
        {[
          { id: 'staking', label: 'Staking', icon: Lock },
          { id: 'farming', label: 'Yield Farming', icon: Coins },
          { id: 'token', label: 'HBX Token', icon: Star },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as any)}
            className={cn(
              "flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all",
              activeTab === tab.id
                ? "bg-primary/10 text-primary"
                : "text-text-secondary hover:bg-white/[0.04] hover:text-text-primary"
            )}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Staking Tab */}
      {activeTab === 'staking' && (
        <div className="grid grid-cols-3 gap-6">
          {/* Staking Form */}
          <div className="col-span-2 space-y-6">
            {/* Lock Period Selection */}
            <div className="card p-6">
              <h3 className="font-heading text-lg font-semibold text-text-primary mb-4">
                Select Lock Period
              </h3>
              <div className="grid grid-cols-5 gap-3">
                {stakingTiers.map((tier, idx) => (
                  <button
                    key={tier.name}
                    onClick={() => setSelectedTier(idx)}
                    className={cn(
                      "p-4 rounded-xl border transition-all text-left",
                      selectedTier === idx
                        ? "border-primary bg-primary/10"
                        : "border-white/[0.06] hover:border-white/[0.15] bg-white/[0.02]"
                    )}
                  >
                    <div className={cn(
                      "font-semibold mb-1",
                      selectedTier === idx ? "text-primary" : "text-text-primary"
                    )}>
                      {tier.name}
                    </div>
                    <div className="text-xs text-text-tertiary mb-2">{tier.duration}</div>
                    <div className="text-sm font-medium text-success">{tier.apy} APY</div>
                    <div className="text-xs text-text-secondary">{tier.multiplier}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Amount Input */}
            <div className="card p-6">
              <h3 className="font-heading text-lg font-semibold text-text-primary mb-4">
                Stake Amount
              </h3>
              <div className="relative mb-4">
                <input
                  type="number"
                  value={stakeAmount}
                  onChange={(e) => setStakeAmount(e.target.value)}
                  placeholder="0.00"
                  className="input text-2xl font-mono pr-24"
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
                  <span className="text-text-secondary font-medium">HBX</span>
                  <button
                    onClick={() => setStakeAmount(walletBalance.toString())}
                    className="text-xs text-primary hover:text-primary-hover font-medium"
                  >
                    MAX
                  </button>
                </div>
              </div>
              <div className="flex justify-between text-sm text-text-secondary mb-6">
                <span>Available: {walletBalance.toLocaleString()} HBX</span>
                <span>≈ ${(parseFloat(stakeAmount || '0') * 0.15).toFixed(2)}</span>
              </div>

              {/* Stake Summary */}
              <div className="bg-background-tertiary rounded-xl p-4 mb-6">
                <div className="flex justify-between py-2 border-b border-white/[0.06]">
                  <span className="text-text-secondary">Lock Period</span>
                  <span className="text-text-primary font-medium">{stakingTiers[selectedTier].duration}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-white/[0.06]">
                  <span className="text-text-secondary">Multiplier</span>
                  <span className="text-primary font-medium">{stakingTiers[selectedTier].multiplier}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-white/[0.06]">
                  <span className="text-text-secondary">Estimated APY</span>
                  <span className="text-success font-medium">{stakingTiers[selectedTier].apy}</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-text-secondary">Unlock Date</span>
                  <span className="text-text-primary font-medium">
                    {new Date(Date.now() + parseInt(stakingTiers[selectedTier].duration) * 24 * 60 * 60 * 1000).toLocaleDateString()}
                  </span>
                </div>
              </div>

              <button 
                className="btn-primary btn-lg w-full"
                disabled={!isContractDeployed}
              >
                <Lock className="h-5 w-5" />
                {isContractDeployed ? 'Stake HBX' : 'Contracts Not Deployed'}
              </button>

              <p className="text-xs text-text-tertiary text-center mt-4">
                <Info className="h-3 w-3 inline mr-1" />
                Must stake at least 7 days before month end to qualify for rewards
              </p>
            </div>
          </div>

          {/* Sidebar - Revenue & Distribution */}
          <div className="space-y-6">
            {/* Next Distribution */}
            <div className="card p-6">
              <div className="flex items-center gap-2 mb-4">
                <Calendar className="h-5 w-5 text-primary" />
                <h3 className="font-heading text-lg font-semibold text-text-primary">
                  Next Distribution
                </h3>
              </div>
              {isContractDeployed ? (
                <>
                  <div className="text-center py-6">
                    <div className="font-heading text-4xl font-bold text-text-primary mb-2">
                      {daysUntilDistribution}
                    </div>
                    <div className="text-text-secondary">days remaining</div>
                  </div>
                  <div className="bg-background-tertiary rounded-xl p-4">
                    <div className="flex justify-between mb-2">
                      <span className="text-text-secondary">Pool Size</span>
                      <span className="text-text-primary font-medium">$0</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-text-secondary">Total Staked</span>
                      <span className="text-text-primary font-medium">0 HBX</span>
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center py-8">
                  <XCircle className="h-10 w-10 text-text-tertiary mx-auto mb-3" />
                  <p className="text-text-secondary text-sm">No blockchain data</p>
                  <p className="text-text-tertiary text-xs mt-1">Deploy contracts to see metrics</p>
                </div>
              )}
            </div>

            {/* Revenue Sources */}
            <div className="card p-6">
              <div className="flex items-center gap-2 mb-4">
                <BarChart3 className="h-5 w-5 text-accent" />
                <h3 className="font-heading text-lg font-semibold text-text-primary">
                  Revenue Sources
                </h3>
              </div>
              {isContractDeployed ? (
                <div className="space-y-3">
                  {revenueSources.map((source) => (
                    <div key={source.name} className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                        <source.icon className="h-4 w-4 text-primary" />
                      </div>
                      <div className="flex-1">
                        <div className="flex justify-between mb-1">
                          <span className="text-sm text-text-primary">{source.name}</span>
                          <span className="text-sm font-medium text-text-primary">$0</span>
                        </div>
                        <div className="h-1.5 bg-background-tertiary rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-primary to-accent rounded-full"
                            style={{ width: '0%' }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="space-y-3">
                  {revenueSources.map((source) => (
                    <div key={source.name} className="flex items-center gap-3 opacity-50">
                      <div className="w-8 h-8 rounded-lg bg-background-tertiary flex items-center justify-center">
                        <source.icon className="h-4 w-4 text-text-tertiary" />
                      </div>
                      <div className="flex-1">
                        <div className="flex justify-between mb-1">
                          <span className="text-sm text-text-secondary">{source.name}</span>
                          <span className="text-sm font-medium text-text-tertiary">—</span>
                        </div>
                        <div className="h-1.5 bg-background-tertiary rounded-full" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Your Rewards */}
            <div className="card p-6">
              <h3 className="font-heading text-lg font-semibold text-text-primary mb-4">
                Your Estimated Rewards
              </h3>
              <div className="text-center py-4">
                <div className="font-heading text-3xl font-bold text-text-tertiary mb-1">
                  {isContractDeployed ? '0.00 HBX' : '—'}
                </div>
                <div className="text-text-tertiary text-sm">{isContractDeployed ? '≈ $0.00' : 'Awaiting deployment'}</div>
              </div>
              <button className="btn-secondary w-full" disabled>
                <Gift className="h-4 w-4" />
                {isContractDeployed ? `Claim (${daysUntilDistribution} days)` : 'Not Available'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Token Tab */}
      {activeTab === 'token' && (
        <div className="space-y-6">
          {/* Token Overview */}
          <div className="card p-6">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                <span className="text-2xl font-bold text-background-primary">HBX</span>
              </div>
              <div>
                <h2 className="font-heading text-2xl font-bold text-text-primary">HybridX Token</h2>
                <p className="text-text-secondary">Native utility & governance token</p>
              </div>
              <div className="ml-auto text-right">
                {isContractDeployed ? (
                  <>
                    <div className="font-heading text-2xl font-bold text-text-primary">$0.00</div>
                    <div className="text-text-tertiary text-sm">Not listed yet</div>
                  </>
                ) : (
                  <>
                    <div className="font-heading text-2xl font-bold text-text-tertiary">—</div>
                    <div className="text-text-tertiary text-sm">Pre-deployment</div>
                  </>
                )}
              </div>
            </div>

            <div className="grid grid-cols-4 gap-4">
              <div className="bg-background-tertiary rounded-xl p-4">
                <div className="text-text-tertiary text-sm mb-1">Market Cap</div>
                <div className="font-heading text-xl font-bold text-text-primary">{isContractDeployed ? '$0' : '—'}</div>
              </div>
              <div className="bg-background-tertiary rounded-xl p-4">
                <div className="text-text-tertiary text-sm mb-1">Total Supply</div>
                <div className="font-heading text-xl font-bold text-text-primary">1B HBX</div>
                <div className="text-xs text-text-tertiary">(planned)</div>
              </div>
              <div className="bg-background-tertiary rounded-xl p-4">
                <div className="text-text-tertiary text-sm mb-1">Circulating</div>
                <div className="font-heading text-xl font-bold text-text-primary">{isContractDeployed ? '0 HBX' : '—'}</div>
              </div>
              <div className="bg-background-tertiary rounded-xl p-4">
                <div className="text-text-tertiary text-sm mb-1">Staked</div>
                <div className="font-heading text-xl font-bold text-text-primary">{isContractDeployed ? '0 HBX' : '—'}</div>
              </div>
            </div>
          </div>

          {/* Holding Tiers */}
          <div className="card p-6">
            <h3 className="font-heading text-lg font-semibold text-text-primary mb-4">
              Holding Tiers & Benefits
            </h3>
            <div className="overflow-x-auto">
              <table className="table-modern">
                <thead>
                  <tr>
                    <th>Tier</th>
                    <th>Min Holdings</th>
                    <th>Fee Discount</th>
                    <th>Benefits</th>
                  </tr>
                </thead>
                <tbody>
                  {holdingTiers.map((tier) => (
                    <tr key={tier.name} className={tier.name === 'Standard' && isContractDeployed ? 'bg-primary/5' : ''}>
                      <td>
                        <div className="flex items-center gap-2">
                          {tier.name === 'Standard' && isContractDeployed && (
                            <CheckCircle2 className="h-4 w-4 text-primary" />
                          )}
                          <span className={cn(
                            "font-medium",
                            tier.name === 'Standard' && isContractDeployed ? "text-primary" : "text-text-primary"
                          )}>
                            {tier.name}
                          </span>
                        </div>
                      </td>
                      <td className="font-mono">{tier.min} HBX</td>
                      <td className="text-success font-medium">{tier.discount}</td>
                      <td>
                        <div className="flex flex-wrap gap-1">
                          {tier.benefits.map((benefit, i) => (
                            <span
                              key={i}
                              className="px-2 py-0.5 bg-background-tertiary rounded text-xs text-text-secondary"
                            >
                              {benefit}
                            </span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Tokenomics */}
          <div className="card p-6">
            <h3 className="font-heading text-lg font-semibold text-text-primary mb-4">
              Tokenomics Distribution
            </h3>
            <div className="grid grid-cols-3 gap-4">
              {[
                { label: 'Staking Rewards', percent: 30, color: '#00d4aa' },
                { label: 'Platform Revenue', percent: 20, color: '#6366f1' },
                { label: 'Team & Dev (Vested)', percent: 15, color: '#f0b90b' },
                { label: 'Liquidity', percent: 15, color: '#1e90ff' },
                { label: 'Marketing', percent: 10, color: '#8b5cf6' },
                { label: 'Community', percent: 10, color: '#f6465d' },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-3">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: item.color }}
                  />
                  <div>
                    <div className="text-sm text-text-primary">{item.label}</div>
                    <div className="text-lg font-bold text-text-primary">{item.percent}%</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Farming Tab Placeholder */}
      {activeTab === 'farming' && (
        <div className="card p-12 text-center">
          <Coins className="h-16 w-16 mx-auto text-text-tertiary mb-4" />
          <h3 className="font-heading text-xl font-bold text-text-primary mb-2">
            Yield Farming Coming Soon
          </h3>
          <p className="text-text-secondary max-w-md mx-auto">
            Provide liquidity to HBX pairs on Uniswap and earn additional rewards.
            Launch expected Q2 2026.
          </p>
        </div>
      )}
    </div>
  );
}
